'use client'

import { XIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { OverflowAccountTabs } from '@/components/widget/overflow-account-tabs'
import { getExchangeIconKey, getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'
import { PublicAccount } from '@/types/generated/common/account'
import { Exchange } from '@/types/generated/common/types'

interface ExchangeAccountTabBarProps {
  exchange: Exchange
  accounts: PublicAccount[]
  activeAccountId: string | undefined
  onSelectAccount: (accountId: string) => void
  /** Overrides the bar's own horizontal inset (default `px-3`) — the caller
   * should match whatever padding its own rows/content use, so the bar's
   * left/right edges line up with them instead of the bar carrying its own,
   * independently-tuned inset. */
  className?: string
  /** Drop the border/background on every inactive account tab — see
   * AccountTab's own `plainInactive` doc comment. */
  plainInactiveTabs?: boolean
  /** Renders an X at the bar's right end that removes every row this bar heads. */
  onRemoveAll?: () => void
  /** Accessible name / tooltip for the X; required alongside `onRemoveAll`. */
  removeAllLabel?: string
}

/** One row: exchange icon fixed on the left (so it's shown once, not
 * repeated per account tab — see AccountTab's `hideIcon`), then the
 * account tabs filling the rest; tabs that don't fit collapse into a "+N"
 * dropdown (see OverflowAccountTabs) rather than scrolling. Shared by every
 * single-exchange account picker (order form sections, Quick Order, One-Tap
 * Buy, single hedge) so they stay visually and behaviorally identical. */
export function ExchangeAccountTabBar({
  exchange,
  accounts,
  activeAccountId,
  onSelectAccount,
  className,
  plainInactiveTabs,
  onRemoveAll,
  removeAllLabel,
}: ExchangeAccountTabBarProps) {
  const iconKey = getExchangeIconKey(exchange)

  return (
    <div
      data-tutorial="orderAccounts"
      className={cn('account-tab-strip flex h-fit items-end gap-3 px-3 pt-1.5', className)}
    >
      <span className="flex h-fit shrink-0 items-center pb-1" title={getExchangeLabel(exchange)}>
        {iconKey && <ExchangeIcon exchange={iconKey} className="size-4 shrink-0 rounded-full" />}
      </span>
      <OverflowAccountTabs
        accounts={accounts}
        activeAccountId={activeAccountId}
        onSelectAccount={onSelectAccount}
        className="z-10 -ml-2 gap-1 pt-2"
        hideIcon
        plainInactive={plainInactiveTabs}
        maxWidthClassName="max-w-48"
      />
      {onRemoveAll && (
        <Button
          type="button"
          variant="subtle"
          size="icon"
          aria-label={removeAllLabel}
          title={removeAllLabel}
          onClick={onRemoveAll}
          className="mb-0.5 size-6 shrink-0 p-1"
        >
          <XIcon className="size-3.5" />
        </Button>
      )}
    </div>
  )
}
